// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, only use the separate dev API server when running on localhost.
  // This avoids mixed-content/CORS issues if NODE_ENV=development is used on a remote/staging host.
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  return marketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : `${window.location.origin}`;
};

export const uploadFile = body => {
  return post('/api/upload-file', body);
};

export const deleteFile = body => {
  // Send as JSON since server expects req.body to be parsed JSON
  return request('/api/delete-file', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body != null;
  const bodyMaybe = shouldSerializeBody
    ? { body: serialize(body) }
    : body != null
      ? { body }
      : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  console.log('🔵 [API Request]', options.method || 'GET', url);

  return window.fetch(url, fetchOptions).catch(fetchError => {
    console.error('❌ [Fetch Failed]', {
      url,
      method: options.method || 'GET',
      error: fetchError.message,
      stack: fetchError.stack
    });
    throw fetchError;
  }).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : '';

    if (res.status >= 400) {
      // Parse error body based on content-type. Avoid forcing JSON on HTML/text responses.
      if (contentType === 'application/json') {
        return res
          .json()
          .then(data => {
            const message = data?.message || data?.error || res.statusText || 'Request failed';
            const err = Object.assign(new Error(message), data, { status: res.status });
            throw err;
          })
          .catch(() => {
            // If JSON parsing itself failed, fall back to text
            return res.text().then(text => {
              const err = new Error(text || res.statusText || 'Request failed');
              err.status = res.status;
              throw err;
            });
          });
      }
      if (contentType === 'application/transit+json') {
        return res.text().then(t => {
          try {
            const data = deserialize(t);
            const message = data?.message || res.statusText || 'Request failed';
            const err = Object.assign(new Error(message), data, { status: res.status });
            throw err;
          } catch (e) {
            const err = new Error(res.statusText || 'Request failed');
            err.status = res.status;
            throw err;
          }
        });
      }
      // Fallback: treat as text (e.g., HTML error page)
      return res.text().then(text => {
        const snippet = (text || '').slice(0, 300); // avoid huge payloads
        const err = new Error(snippet || res.statusText || 'Request failed');
        err.status = res.status;
        throw err;
      });
    }

    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

// JSON POST helper for custom endpoints
const postJson = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  };
  // Debug: log outgoing JSON POSTs to help trace issues
  try {
    // Avoid logging large blobs
    const preview = typeof body === 'object' ? JSON.parse(JSON.stringify(body)) : body;
    // eslint-disable-next-line no-console
    console.log('[api.postJson] ->', path, preview);
  } catch (_) {
    // ignore logging failures
  }
  return request(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};


// Get all users for admin to review
export const getAllUsers = (body) => {
  return post('/api/admin/users', body); // Options for Paginations
};

export const getUserListings = (body) => {
  return post('/api/user/listing', body);
}

// Get status wise users for admin to review
export const getStatusWiseUsers = (body) => {
  return post('/api/admin/users/status', body);
};

// Approve user
export const approveUser = (body) => {
  return post('/api/admin/users/approve', body);
};

// Reject user
export const rejectUser = (body) => {
  return post('/api/admin/users/reject', body);
};

// Update listing
export const updateListing = (body) => {
  return post('/api/admin/listing/update', body);
};

// Set or unset Super Admin for a user
export const setSuperAdmin = (body) => {
  return post('/api/admin/users/set-superadmin', body);
};

export const autoApproveBlogListings = (body) => {
  return post('/api/admin/listings/approve-blogs', body);
};


// ZOOM
export const zoomSchedule = body => {
  return post('/api/zoom/schedule', body);
};

export const zoomCallback = body => {
  return post('/api/zoom/callback', body);
};

export const zoomCreateMeetingWithRefreshToken = body => {
  return post('/api/zoom/create-meeting-with-refresh-token', body);
};

export const createZoomSignature = body => {
  return post('/api/zoom/create-signature', body);
};

export const createMeetingWithServerAccount = body => {
  return post('/api/zoom/create-meeting-with-server-account', body);
}

// Record a Zoom join initiated from the app (JSON)
export const zoomJoin = body => {
  return postJson('/api/zoom/join', body);
}
// Ratings: recompute listing rating summary from reviews and persist to listing.publicData
export const recomputeListingRatings = body => {
  return post('/api/ratings/recompute', body);
};
// Wallet API
export const walletHold = body => {
  return post('/api/wallet/hold', body);
}

export const walletRelease = body => {
  return post('/api/wallet/release', body);
}

export const walletCapture = body => {
  return post('/api/wallet/capture', body);
}
export const walletDebit = body => {
  return post('/api/wallet/debit', body);
}
export const walletCredit = body => {
  return post('/api/wallet/credit', body);
}
export const walletBalance = body => {
  return post('/api/wallet/balance', body);
}
export const walletHistory = body => {
  return post('/api/wallet/history', body);
}

// Wallet summary
export const walletSummary = body => {
  return post('/api/wallet/summary', body);
}

// Reward API
export const rewardSummary = body => {
  return post('/api/reward/summary', body);
}

export const rewardHistory = body => {
  return post('/api/reward/history', body);
}

export const rewardDebit = body => {
  return post('/api/reward/debit', body);
}
// Record a login activity to update streak on login
export const rewardLoginActivity = body => {
  return post('/api/reward/login-activity', body);
}

// Withdrawal Request Flow
export const walletWithdrawRequest = body => {
  return post('/api/wallet/withdraw/request', body);
};

// Admin endpoints for withdrawals
export const adminListWithdrawals = body => {
  return post('/api/admin/wallet/withdrawals', body);
};
export const adminApproveWithdrawal = body => {
  return post('/api/admin/wallet/withdrawals/approve', body);
};
export const adminRejectWithdrawal = body => {
  return post('/api/admin/wallet/withdrawals/reject', body);
};
export const adminConfirmWithdrawal = body => {
  return post('/api/admin/wallet/withdrawals/confirm', body);
};

// Get refresh token
export const getRefreshToken = body => {
  return post('/api/get-refresh-token', body);
};

// Fetch provider & customer emails (Integration API via server)
export const getTransactionParties = body => {
  // This endpoint returns JSON
  return postJson('/api/get-transaction-parties', body);
};

// Create Google Calendar event via backend route
export const createGoogleEvent = body => {
  return postJson('/api/google/create-event', body);
};
export const deleteGoogleEvent = body => {
  return postJson("/api/google/delete-event", body)
};
// Create Google Calendar event via backend route
export const createReferralCode = body => {
  return post('/api/create-referral-code', body);
};

export const checkUserReferralCode = body => {
  return post('/api/check-referral-code', body);
};

export const validateReferralCode = body => {
  return post('/api/validate-referral-code', body);
};

export const updateReferralOnSignup = body => {
  return post('/api/update-referral-on-signup', body);
};

// Admin: list referrals
export const adminListReferrals = body => {
  return post('/api/admin/referrals', body);
};
export const adminblogsList = body => {
  return post('/api/admin/blogs/listings', body);
};
// Admin: show single listing (includes drafts) via Integration SDK
export const adminShowListing = body => {
  return post('/api/admin/listings/show', body);
};
export const adminOpenListing = body => {
  return post('/api/admin/listings/open', body);
};
export const adminCloseListing = body => {
  return post('/api/admin/listings/close', body);
};

// tap payment gateway api

export const onFetchCustomerCards = body => {
  return post('/api/fetch-customer-cards', body);
};

export const onRetrieveCharge = body => {
  return post('/api/retrieve-charge', body);
};

export const onCreateTokenForSavedCard = body => { 
  return postJson('/api/create-token-for-saved-cards', body);
};

export const onCreateCharge = body => {
  return post('/api/create-charge', body);
};

export const updatePaymentInDB = body => {
  return post('/api/updatePaymentInDB', body);
};

export const deleteCard = body => {
  return post('/api/deleteCard', body);
};


// Initialize a payment record before calling payment gateway
export const initPaymentInDB = body => {
  return postJson('/api/payment/init', body);
};

// Trigger Trial Auto Complete flow on the server (JSON)
// This nudges server to run runTrialAutoComplete safely with server credentials
export const trialAutoCompleteTrigger = async body => {

  // Swallow 429 cooldown responses to avoid console noise
  try {
    return await postJson('/api/cron/trial-autocomplete', body);
  } catch (err) {
    if (err && err.status === 429) {
      // Resolve gracefully to indicate cooldown; caller can ignore or adapt
      return { status: 'cooldown', retryAfterMs: err.retryAfterMs };
    }
    throw err;
  }
};
